import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { notification } from 'antd';
import { makePrioStyles } from '../../../theme/utils';
import Flex from '../../../components/Flex';
import { TimeAndLeaveManagementTimelineItem } from '../selectors';
import TimeKeepingEditTimeRecordForm, {
  TimeRecordFormModel,
} from '../../timeKeeping/components/TimeKeepingEditTimeRecordForm';
import {
  MonthlyCloseId,
  TimeKeepingDayId,
  TimeKeepingDayType,
  TimeRecordId,
} from '../../../models/Types';
import { isTemporaryId } from '../../../util';
import {
  CreateTimeRecordRequest,
  TimeRecord,
  UpdateTimeRecordRequest,
} from '../../../models/TimeRecord';
import { useDispatch, useSelector } from 'react-redux';
import { getUserMe } from '../../../apps/main/rootReducer';
import {
  apiCreateTimeRecord,
  apiDeleteTimeRecords,
  apiUpdateTimeRecord,
} from '../../timeRecords/api';
import {
  createTimeRecords,
  deleteMyTimeRecord,
  updateTimeRecord,
} from '../../timeRecords/actions';
import { useTranslation } from 'react-i18next';
import TimeKeepingEditForm, {
  TimeKeepingFormModel,
} from '../../timeKeeping/components/TimeKeepingEditForm';
import {
  apiDeleteTimeKeepingDayMe,
  apiFetchTimeKeepingDaySuggestion,
  apiUpdateTimeKeeping,
} from '../../timeKeeping/api';
import {
  TimeKeepingDay,
  TimeKeepingEntry,
  TimeKeepingGeneral,
} from '../../../models/TimeKeeping';
import {
  deleteTimeKeepingDay,
  updateTimeKeepingDay,
} from '../../timeKeeping/actions';
import moment from 'moment';
import { toISOString } from '../../timeKeeping/utils';

const useStyles = makePrioStyles((theme) => ({
  root: {
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: "''",
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: 8,
      backgroundColor: theme.old.palette.backgroundPalette.base,
    },
  },
  form: {
    overflow: 'auto',
    height: '100%',
    width: '100%',
    padding: theme.old.spacing.defaultPadding,
  },
}));

interface TimeAndLeaveTimelineFormManagerProps {
  className?: string;
  selectedItem: TimeAndLeaveManagementTimelineItem;
  timeKeepingGeneralSettings: TimeKeepingGeneral | null;
  onFinish: (
    id: string,
    item: TimeAndLeaveManagementTimelineItem,
    type: 'timeKeeping' | 'timeRecord'
  ) => void;
  onCancel: (
    item: TimeAndLeaveManagementTimelineItem,
    type: 'timeKeeping' | 'timeRecord'
  ) => void;
  onChange: (
    item: TimeAndLeaveManagementTimelineItem,
    type: 'timeKeeping' | 'timeRecord'
  ) => void;
  onDelete: (
    id: string,
    groupId: string,
    type: 'timeKeeping' | 'timeRecord'
  ) => void;
  onSaveTimeKeepingEntries?: (
    item: TimeAndLeaveManagementTimelineItem,
    newItem: TimeAndLeaveManagementTimelineItem
  ) => Promise<TimeAndLeaveManagementTimelineItem | null>;
}

export const TimeAndLeaveTimelineFormManager: React.FC<
  TimeAndLeaveTimelineFormManagerProps
> = (props) => {
  //#region ------------------------------ Defaults
  const {
    className,
    selectedItem,
    timeKeepingGeneralSettings,
    onFinish,
    onCancel,
    onChange,
    onDelete,
    onSaveTimeKeepingEntries,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const userMe = useSelector(getUserMe);

  const [loading, setLoading] = useState<boolean>(false);
  const [formResetKey, setFormResetKey] = useState<number>(0);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOnTimeRecordCreation = useCallback(
    async (value: TimeRecordFormModel, timeRecordId: TimeRecordId) => {
      if (isTemporaryId(timeRecordId)) {
        setLoading(true);
        const request: CreateTimeRecordRequest = {
          projectId: selectedItem.groupId,
          description: value.description,
          day: selectedItem.startDateTime.split('T')[0],
          contactId: userMe.id,
          kilometerDistance: value.kilometerDistance ?? 0,
          title: value.title,
          timeRecordEntries: selectedItem.entries,
          projectPhaseId: value.projectPhaseId,
          isSeparateService: value.isSeparateService,
        };
        await apiCreateTimeRecord([request], request.projectId, true)
          .then(({ data }) => {
            const timeRecord = data[0];
            const sortedEntries = timeRecord.timeRecordEntries.sort((a, b) =>
              moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
            );
            dispatch(createTimeRecords(data));
            onFinish(
              timeRecordId,
              {
                id: timeRecord.timeRecordId,
                groupId: timeRecord.projectId,
                title: timeRecord.title,
                type: 'timeRecord',
                startDateTime: toISOString(sortedEntries[0].startTime),
                endDateTime: toISOString(
                  sortedEntries[sortedEntries.length - 1].endTime
                ),
                entries: timeRecord.timeRecordEntries,
                value: timeRecord,
                state: 'ok',
              },
              'timeRecord'
            );
            apiFetchTimeKeepingDaySuggestion(
              toISOString(timeRecord.timeRecordEntries[0].startTime),
              toISOString(
                timeRecord.timeRecordEntries[
                  timeRecord.timeRecordEntries.length - 1
                ].endTime
              )
            );
          })
          .catch((error) => {
            notification.open({
              message: t('common:error'),
              description: t('timeRecords:errorMessages.createError'),
            });
          });

        setLoading(false);
      }
    },
    [selectedItem, userMe, dispatch, onFinish, t]
  );

  const handleOnTimeRecordChange = useCallback(
    async (
      value: TimeRecordFormModel,
      item: TimeAndLeaveManagementTimelineItem
    ) => {
      let newItem: TimeAndLeaveManagementTimelineItem = {
        ...item,
        value: {
          ...item.value,
          ...value,
        },
      };
      const _value: TimeRecord = newItem.value as TimeRecord;

      const request: UpdateTimeRecordRequest = {
        timeRecordId: _value.timeRecordId,
        projectId: _value.projectId,
        subProjectId: _value.subProjectId,
        description: _value.description,
        day: _value.day,
        contactId: _value.contactId,
        kilometerDistance: _value.kilometerDistance ?? 0,
        title: _value.title,
        timeRecordEntries: _value.timeRecordEntries,
        rowVersion: _value.rowVersion,
        projectPhaseId: _value.projectPhaseId,
        isSeparateService: _value.isSeparateService,
      };

      const { data } = await apiUpdateTimeRecord(
        request,
        _value.timeRecordId,
        _value.rowVersion,
        'me'
      );
      if (data) {
        const sortedEntries = data.timeRecordEntries.sort((a, b) =>
          moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
        );
        dispatch(
          updateTimeRecord(data, data.timeRecordId, item.value as TimeRecord)
        );
        onChange(
          {
            id: data.timeRecordId,
            groupId: data.projectId,
            title: data.title,
            type: 'timeRecord',
            startDateTime: toISOString(sortedEntries[0].startTime),
            endDateTime: toISOString(
              sortedEntries[sortedEntries.length - 1].endTime
            ),
            entries: data.timeRecordEntries,
            value: data,
            state: 'ok',
          },
          'timeRecord'
        );
      } else {
        notification.open({
          message: t('common:error'),
          description: t('timeRecords:errorMessages.updateError'),
        });
      }
    },
    [onChange, dispatch, t]
  );

  const handleOnTimeRecordFormFinish = useCallback(
    (
      value: TimeRecordFormModel,
      timeRecord: TimeAndLeaveManagementTimelineItem
    ) => {
      if (isTemporaryId(timeRecord.id)) {
        handleOnTimeRecordCreation(value, timeRecord.id);
      } else {
        handleOnTimeRecordChange(value, timeRecord);
      }
    },
    [handleOnTimeRecordChange, handleOnTimeRecordCreation]
  );

  const handleOnTimeRecordFormCancel = useCallback(
    (item: TimeAndLeaveManagementTimelineItem) => {
      onCancel(item, 'timeRecord');
    },
    [onCancel]
  );

  const handleOnTimeRecordDelete = useCallback(
    async (timeRecordId: TimeRecordId) => {
      setLoading(true);
      const { result } = await apiDeleteTimeRecords(
        timeRecordId,
        selectedItem?.value as TimeRecord,
        'me'
      );
      if (result.status >= 200 && result.status < 300) {
        dispatch(
          deleteMyTimeRecord(
            timeRecordId,
            selectedItem?.value as TimeRecord,
            true
          )
        );
        onDelete(timeRecordId, selectedItem.groupId, 'timeRecord');
      }
      setLoading(false);
    },
    [onDelete, selectedItem, dispatch]
  );

  const saveTimeKeepingEntries = useCallback(
    async (
      item: TimeAndLeaveManagementTimelineItem,
      updatedEntries: TimeKeepingEntry[],
      extraFields?: { type?: TimeKeepingDayType; notes?: string }
    ): Promise<boolean> => {
      if (onSaveTimeKeepingEntries) {
        const newItem: TimeAndLeaveManagementTimelineItem = {
          ...item,
          entries: updatedEntries,
          value: {
            ...item.value,
            ...(extraFields?.type !== undefined && { type: extraFields.type }),
            ...(extraFields?.notes !== undefined && {
              notes: extraFields.notes,
            }),
            timeKeepingEntries: updatedEntries,
          },
        };
        const result = await onSaveTimeKeepingEntries(item, newItem);
        if (result) {
          onChange(result, 'timeKeeping');
          apiFetchTimeKeepingDaySuggestion(
            toISOString(result.startDateTime),
            toISOString(result.endDateTime)
          );
          return true;
        }
        return false;
      }

      const timeKeepingDay = item.value as TimeKeepingDay;
      const { data } = await apiUpdateTimeKeeping(item.id, {
        type: extraFields?.type ?? timeKeepingDay.type,
        notes: extraFields?.notes ?? timeKeepingDay.notes,
        timeKeepingEntries: updatedEntries,
      });

      if (data) {
        const sortedEntries = [...data.timeKeepingEntries].sort((a, b) =>
          moment(a.startTime).isBefore(moment(b.startTime)) ? -1 : 1
        );
        dispatch(updateTimeKeepingDay(data, item.id));
        onChange(
          {
            id: data.timeKeepingDayId,
            groupId: item.groupId,
            title: '',
            type: 'timeKeeping',
            startDateTime: toISOString(sortedEntries[0].startTime),
            endDateTime: toISOString(
              sortedEntries[sortedEntries.length - 1].endTime
            ),
            entries: data.timeKeepingEntries,
            value: data,
            state: 'ok',
            notes: data.notes,
          } as TimeAndLeaveManagementTimelineItem,
          'timeKeeping'
        );
        apiFetchTimeKeepingDaySuggestion(
          toISOString(sortedEntries[0].startTime),
          toISOString(sortedEntries[sortedEntries.length - 1].endTime)
        );
        return true;
      }
      return false;
    },
    [onSaveTimeKeepingEntries, onChange, dispatch]
  );

  const handleOnTimeKeepingChange = useCallback(
    async (
      value: TimeKeepingFormModel,
      item: TimeAndLeaveManagementTimelineItem
    ) => {
      setLoading(true);

      // Store original values for rollback on error
      const originalItem = { ...item };

      const timeFields: (keyof TimeKeepingFormModel)[] = [
        'fromHour',
        'fromMinute',
        'toHour',
        'toMinute',
        'breaks',
      ];
      const hasTimeChanges = timeFields.some((f) => value[f] !== undefined);

      let updatedEntries = [...(item.entries || [])] as TimeKeepingEntry[];

      if (hasTimeChanges) {
        const baseDate = moment(
          updatedEntries[0]?.startTime || item.startDateTime
        );
        const dateString = baseDate.toISOString(true).split('T')[0];

        if (value.fromHour !== undefined && value.fromMinute !== undefined) {
          updatedEntries[0] = {
            ...updatedEntries[0],
            startTime: `${dateString}T${String(value.fromHour).padStart(2, '0')}:${String(value.fromMinute).padStart(2, '0')}:00`,
          };
        }

        if (value.toHour !== undefined && value.toMinute !== undefined) {
          updatedEntries[updatedEntries.length - 1] = {
            ...updatedEntries[updatedEntries.length - 1],
            endTime: `${dateString}T${String(value.toHour).padStart(2, '0')}:${String(value.toMinute).padStart(2, '0')}:00`,
          };
        }

        // Handle break durations
        const originalEntries = item.entries || [];
        const startTime = moment(updatedEntries[0]?.startTime);
        const endTime = moment(
          updatedEntries[updatedEntries.length - 1]?.endTime
        );
        const fmt = (m: moment.Moment) =>
          `${dateString}T${m.format('HH:mm:ss')}`;

        if (value.breaks !== undefined) {
          const breaks = value.breaks ?? [];
          const minWorkSegment = 1;

          if (
            breaks.length === 0 ||
            breaks.every((b) => (b.hours ?? 0) * 60 + (b.minutes ?? 0) === 0)
          ) {
            // No breaks — collapse to single entry
            updatedEntries = [
              {
                ...updatedEntries[0],
                startTime: fmt(startTime),
                endTime: fmt(endTime),
              },
            ];
          } else {
            const newEntries = [...updatedEntries];
            let prevBreakEnd = startTime;
            for (let i = 0; i < breaks.length; i++) {
              const breakMinutes =
                (breaks[i].hours ?? 0) * 60 + (breaks[i].minutes ?? 0);
              if (breakMinutes === 0) continue;

              // Use the original boundary for this entry as the break start anchor,
              // but only if it exists and falls after the previous break ended.
              // Fall back to the midpoint of the remaining segment.
              const segmentEnd =
                i + 1 < newEntries.length
                  ? moment(newEntries[i + 1]?.endTime ?? endTime)
                  : endTime;
              const anchorBreakStart =
                originalEntries[i]?.endTime &&
                moment(originalEntries[i].endTime).isAfter(prevBreakEnd)
                  ? moment(originalEntries[i].endTime)
                  : moment(prevBreakEnd).add(
                      Math.floor(segmentEnd.diff(prevBreakEnd, 'minutes') / 2),
                      'minutes'
                    );

              const breakStart = moment.max(
                anchorBreakStart,
                moment(prevBreakEnd).add(minWorkSegment, 'minutes')
              );
              const breakEnd = moment(breakStart).add(breakMinutes, 'minutes');

              // Validate: breakEnd must be before endTime with room for a work segment
              if (
                breakEnd.isSameOrAfter(
                  moment(endTime).subtract(minWorkSegment, 'minutes')
                )
              ) {
                setFormResetKey((prev) => prev + 1);
                notification.open({
                  message: t('common:error'),
                  description: t(
                    'timeKeeping:messages.errorMessages.breakExceedsWorkTime'
                  ),
                });
                setLoading(false);
                return;
              }

              // Validate: for non-final breaks, breakEnd must leave room for a work
              // segment before the next break's segment ends (catches a break duration
              // that squashes the gap between two breaks instead of silently clamping).
              if (
                i + 1 < breaks.length &&
                breakEnd.isSameOrAfter(
                  moment(segmentEnd).subtract(minWorkSegment, 'minutes')
                )
              ) {
                setFormResetKey((prev) => prev + 1);
                notification.open({
                  message: t('common:error'),
                  description: t(
                    'timeKeeping:messages.errorMessages.breakExceedsWorkTime'
                  ),
                });
                setLoading(false);
                return;
              }

              // Guard: the entry following this break must exist — breaks.length
              // should always be newEntries.length - 1, but enforce it explicitly
              // to prevent writing undefined into the array and sending malformed
              // data to the API.
              if (i + 1 >= newEntries.length) {
                setFormResetKey((prev) => prev + 1);
                notification.open({
                  message: t('common:error'),
                  description: t(
                    'timeKeeping:messages.errorMessages.updateError'
                  ),
                });
                setLoading(false);
                return;
              }

              // Spread from the current entry (not always entry 0) to preserve its fields
              const {
                startTime: _s,
                endTime: _e,
                ...baseEntry
              } = newEntries[i];
              newEntries[i] = { ...newEntries[i], endTime: fmt(breakStart) };
              newEntries[i + 1] = {
                ...baseEntry,
                ...newEntries[i + 1],
                startTime: fmt(breakEnd),
                endTime:
                  i + 1 === breaks.length
                    ? fmt(endTime)
                    : newEntries[i + 1].endTime,
              };

              prevBreakEnd = breakEnd;
            }
            updatedEntries = newEntries;
          }
        }
      }

      // Defensive check: verify every entry has endTime strictly after
      // startTime and that consecutive entries don't overlap.  This catches
      // any edge-case that slips past the per-break validations above.
      for (let j = 0; j < updatedEntries.length; j++) {
        const entry = updatedEntries[j];
        if (moment(entry.endTime).isSameOrBefore(moment(entry.startTime))) {
          setFormResetKey((prev) => prev + 1);
          notification.open({
            message: t('common:error'),
            description: t(
              'timeKeeping:messages.errorMessages.breakExceedsWorkTime'
            ),
          });
          setLoading(false);
          return;
        }
        if (j > 0) {
          const prev = updatedEntries[j - 1];
          if (moment(entry.startTime).isBefore(moment(prev.endTime))) {
            setFormResetKey((prev) => prev + 1);
            notification.open({
              message: t('common:error'),
              description: t(
                'timeKeeping:messages.errorMessages.breakExceedsWorkTime'
              ),
            });
            setLoading(false);
            return;
          }
        }
      }

      const success = await saveTimeKeepingEntries(item, updatedEntries, {
        type: value.type,
        notes: value.notes,
      });

      if (!success) {
        // Rollback to original values on error - force form remount
        setFormResetKey((prev) => prev + 1);
        onChange(originalItem, 'timeKeeping');
        notification.open({
          message: t('common:error'),
          description: t('timeKeeping:messages.errorMessages.updateError'),
        });
      }
      setLoading(false);
    },
    [saveTimeKeepingEntries, onChange, t]
  );

  const handleOnTimeKeepingDeleteBreak = useCallback(
    async (breakIndex: number) => {
      if (!selectedItem) return;
      setLoading(true);

      const originalItem = { ...selectedItem };
      const entries = [...(selectedItem.entries || [])];

      // Merge entries[breakIndex] and entries[breakIndex + 1] by keeping
      // entries[breakIndex].startTime and entries[breakIndex + 1].endTime
      const mergedEntry = {
        ...entries[breakIndex],
        endTime:
          entries[breakIndex + 1]?.endTime ?? entries[breakIndex].endTime,
      };
      const updatedEntries = [
        ...entries.slice(0, breakIndex),
        mergedEntry,
        ...entries.slice(breakIndex + 2),
      ] as TimeKeepingEntry[];

      const success = await saveTimeKeepingEntries(
        selectedItem,
        updatedEntries
      );

      if (!success) {
        setFormResetKey((prev) => prev + 1);
        onChange(originalItem, 'timeKeeping');
        notification.open({
          message: t('common:error'),
          description: t('timeKeeping:messages.errorMessages.updateError'),
        });
      }
      setLoading(false);
    },
    [selectedItem, saveTimeKeepingEntries, onChange, t]
  );

  const handleOnTimeKeepingDelete = useCallback(
    async (
      monthlyCloseId: MonthlyCloseId,
      timeKeepingDayId: TimeKeepingDayId
    ) => {
      setLoading(true);
      const { result } = await apiDeleteTimeKeepingDayMe(timeKeepingDayId);

      if (result.status >= 200 && result.status < 300) {
        dispatch(deleteTimeKeepingDay(monthlyCloseId, timeKeepingDayId));
        onDelete(timeKeepingDayId, selectedItem.groupId, 'timeKeeping');
      }
      setLoading(false);
    },
    [selectedItem, onDelete, dispatch]
  );
  //#endregion

  //#region ------------------------------ Components
  const content = useMemo(() => {
    switch (selectedItem.type) {
      case 'timeKeeping': {
        return (
          <TimeKeepingEditForm
            key={`timeKeeping-${selectedItem.id}-${formResetKey}`}
            className={classes.form}
            selectedItem={selectedItem}
            onChange={handleOnTimeKeepingChange}
            onDelete={handleOnTimeKeepingDelete}
            onDeleteBreak={handleOnTimeKeepingDeleteBreak}
            loading={loading}
            timeKeepingGeneralSettings={timeKeepingGeneralSettings}
          />
        );
      }
      case 'timeRecord': {
        return (
          <TimeKeepingEditTimeRecordForm
            className={classes.form}
            selectedItem={selectedItem}
            onCancel={handleOnTimeRecordFormCancel}
            onFinish={handleOnTimeRecordFormFinish}
            onDelete={handleOnTimeRecordDelete}
            loading={loading}
          />
        );
      }
      default: {
        return null;
      }
    }
  }, [
    loading,
    selectedItem,
    classes,
    handleOnTimeRecordFormCancel,
    handleOnTimeRecordFormFinish,
    handleOnTimeRecordDelete,
    handleOnTimeKeepingChange,
    handleOnTimeKeepingDelete,
    formResetKey,
    timeKeepingGeneralSettings,
    handleOnTimeKeepingDeleteBreak,
  ]);
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (selectedItem) {
      setLoading(false);
    }
  }, [selectedItem]);
  //#endregion

  return (
    <Flex.Item className={classNames(classes.root, className)}>
      {content}
    </Flex.Item>
  );
};

export default TimeAndLeaveTimelineFormManager;
